import { BaseTable } from "@/components/shared/data-table";
import { ServerSideDataTablePagination } from "@/components/shared/tables/server-side-table-pagination";
import { useNavigateToInvoiceWithBrowsingQueue } from "@/hooks/useNavigateToInvoiceWithBrowsingQueue";
import type { RawFilters } from "@/hooks/useQueryFilters";
import { useTenant } from "@/providers/session";
import type { GetAllInvoiceInvoice } from "@/queries/invoices/get-all";
import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef, Row } from "@tanstack/react-table";
import { useRef } from "react";
import { useInvoicesForTable } from "../use-invoices-for-table";
import {
  BankTransfersFilters,
  type BankTransfersStatusFilters,
} from "./bank-transfers-filters";
import { TableActionBar } from "./bank-transfers-table-action-bar";

type Props = {
  onDelete: (invoiceIds: string[]) => void;
  onPaid: (invoiceIds: string[]) => void;
  tab: BankTransfersStatusFilters;
  emptyStateComponent?: React.ReactNode;
  guidingMessage: string;
  storageKey: string;
  defaultFilters: RawFilters<any>;
  columns: ColumnDef<GetAllInvoiceInvoice>[];
};

export const BankTransfersDataTable = ({
  onDelete,
  onPaid,
  tab,
  emptyStateComponent,
  guidingMessage,
  storageKey,
  defaultFilters,
  columns,
}: Props) => {
  const navigate = useNavigate();
  const { companySlug } = useTenant();
  const tableRef = useRef(null);

  const {
    table,
    loading,
    pagination,
    filters,
    setFilters,
    globalSearch,
    setGlobalSearch,
    clickableColumns,
    columnVisibility,
    data,
    stickyColumns,
    footer,
    setPage,
    setLimit,
  } = useInvoicesForTable({ storageKey, defaultFilters, columns });

  const navigateToInvoice = useNavigateToInvoiceWithBrowsingQueue(table);
  return (
    <div className="h-full">
      <div className="flex flex-col gap-2 h-full">
        <p className="text-sm text-gray-600 mt-2 font-medium">
          {guidingMessage}
        </p>
        <BankTransfersFilters
          tab={tab}
          filters={filters}
          setFilters={setFilters}
          globalSearch={globalSearch}
          setGlobalSearch={setGlobalSearch}
          table={table}
        />
        <BaseTable
          ref={tableRef}
          table={table}
          clickableColumns={clickableColumns}
          columnVisibility={columnVisibility}
          columns={columns}
          data={data}
          stickyColumns={stickyColumns}
          footer={footer}
          loading={loading}
          onRowClick={handleRowClick}
          emptyStateComponent={emptyStateComponent}
        />
        <div className="mt-0.5" />
        <ServerSideDataTablePagination
          table={table}
          totalItems={pagination.totalItems}
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          pageSize={pagination.itemsPerPage}
          onPageChange={setPage}
          onPageSizeChange={setLimit}
        />
        <TableActionBar
          table={table}
          onDelete={onDelete}
          onPay={handleToPay}
          onPaid={onPaid}
          currentTab={tab}
        />
      </div>
    </div>
  );

  function handleRowClick(
    _event: React.MouseEvent,
    row: Row<GetAllInvoiceInvoice>,
  ) {
    navigateToInvoice(row.original.invoiceId);
  }

  function handleToPay(invoiceIds: string[]) {
    navigate({
      to: "/$company/unternehmen/bezahlen",
      params: { company: companySlug },
      search: {
        invoiceIds,
        navigateBackTo: "ueberweisungen",
      },
    });
  }
};
